/**
 * ChoirVobApplicationsTab — Pending + All VOB Form Applications panel.
 */
import React from 'react';
import {
  FileText, Clock, UserCheck, UserX, Trash2, Loader2,
} from 'lucide-react';

export default function ChoirVobApplicationsTab({
  vobApplications,
  isMainAdmin,
  bulkEnrolling,
  onApprove,
  onDecline,
  onDelete,
  onBulkEnroll,
}) {
  const pending = vobApplications.filter(a => a.status === 'pending');
  const enrollable = vobApplications.filter(a => a.status !== 'declined');

  return (
    <div className="space-y-4">
      {/* Join Choir Link */}
      <div className="bg-indigo-50 rounded-2xl p-5 border border-indigo-200">
        <h3 className="font-bold text-indigo-800 mb-2 flex items-center gap-2">
          <FileText className="w-5 h-5" /> VOB Choir Membership Form Link
        </h3>
        <p className="text-sm text-indigo-600 mb-2">Share this link for people to fill in the choir membership form:</p>
        <div className="p-3 bg-white rounded-xl border border-indigo-200">
          <p className="text-sm font-mono text-indigo-800 break-all" data-testid="choir-join-link">https://cccvob.com/join-choir</p>
        </div>
        <button
          onClick={() => navigator.clipboard.writeText('https://cccvob.com/join-choir')}
          className="mt-2 px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-600 transition-colors"
          data-testid="choir-copy-link-btn"
        >
          Copy Link
        </button>
      </div>

      {/* Pending VOB Applications */}
      {pending.length > 0 && (
        <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" /> Pending VOB Applications ({pending.length})
          </h3>
          <div className="space-y-3">
            {pending.map(app => (
              <div key={app.id} className="bg-white rounded-xl p-4 border border-amber-100" data-testid={`vob-app-${app.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800">{app.full_name}</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {app.departments?.map(d => (
                        <span key={d} className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs">{d}</span>
                      ))}
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">{app.gender}</span>
                      {app.choruses?.map(c => (
                        <span key={c} className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">{c}</span>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{app.phone} {app.email ? `· ${app.email}` : ''}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Submitted: {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => onApprove(app.id)} data-testid={`vob-approve-${app.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600">
                      <UserCheck className="w-3.5 h-3.5" /> Accept
                    </button>
                    <button onClick={() => onDecline(app.id)} data-testid={`vob-decline-${app.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold hover:bg-red-600">
                      <UserX className="w-3.5 h-3.5" /> Decline
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All VOB Applications */}
      <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-100">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="font-bold text-slate-800">All VOB Form Applications ({vobApplications.length})</h3>
          {enrollable.length > 0 && (
            <button
              onClick={onBulkEnroll}
              disabled={bulkEnrolling}
              data-testid="vob-bulk-enroll-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-violet-950 text-sm font-bold shadow hover:from-amber-600 hover:to-yellow-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {bulkEnrolling ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Enrolling…</>
              ) : (
                <><UserCheck className="w-4 h-4" /> Enroll ALL as Approved Members ({enrollable.length})</>
              )}
            </button>
          )}
        </div>
        {vobApplications.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No applications yet. Share the join link to get started.</p>
        ) : (
          <div className="space-y-3">
            {vobApplications.map(app => (
              <div key={app.id} className={`rounded-xl p-4 border transition-all ${
                app.status === 'pending' ? 'border-amber-200 bg-amber-50/50' :
                app.status === 'accepted' ? 'border-emerald-200 bg-emerald-50/30' :
                app.status === 'declined' ? 'border-red-200 bg-red-50/30' :
                'border-slate-100'
              }`} data-testid={`vob-app-row-${app.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-slate-800">{app.full_name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        app.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        app.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-red-100 text-red-700'
                      }`}>{app.status === 'accepted' ? 'Accepted' : app.status === 'declined' ? 'Declined' : 'Pending'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {app.departments?.map(d => (
                        <span key={d} className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs">{d}</span>
                      ))}
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">{app.gender}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">{app.marital_status}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">{app.occupation}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-1 mt-2 text-sm text-slate-600">
                      <p>Phone: {app.phone}</p>
                      {app.email && <p>Email: {app.email}</p>}
                      <p>Birthday: {app.birthday}</p>
                      {app.years_in_ccc && <p>Years in CCC: {app.years_in_ccc}</p>}
                      {app.years_in_vob && <p>Years in VOB: {app.years_in_vob}</p>}
                      {app.previous_chorister === 'Yes' && <p>Prev. Choir: {app.previous_choir_parish} ({app.previous_choir_years} yrs)</p>}
                    </div>
                    {(app.choruses?.length > 0 || app.tune_instruments?.length > 0 || app.percussion_instruments?.length > 0) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {app.choruses?.map(c => <span key={c} className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">{c}</span>)}
                        {app.tune_instruments?.map(i => <span key={i} className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-xs">{i}</span>)}
                        {app.percussion_instruments?.map(i => <span key={i} className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs">{i}</span>)}
                        {app.other_instruments && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">{app.other_instruments}</span>}
                      </div>
                    )}
                    {(app.kin_name || app.elder_name) && (
                      <div className="mt-2 text-xs text-slate-400">
                        {app.kin_name && <span>Ref: {app.kin_name} ({app.kin_relationship}) </span>}
                        {app.elder_name && <span>· Elder: {app.elder_name}</span>}
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-1">Submitted: {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {app.status === 'pending' && (
                      <>
                        <button onClick={() => onApprove(app.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600">
                          <UserCheck className="w-3.5 h-3.5" /> Accept
                        </button>
                        <button onClick={() => onDecline(app.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold hover:bg-red-600">
                          <UserX className="w-3.5 h-3.5" /> Decline
                        </button>
                      </>
                    )}
                    {isMainAdmin && (
                      <button onClick={() => onDelete(app.id)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
